import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, Mail, Lock, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

export default function Login() {
  const { login, verifyLoginOtp, logout, formatApiError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // OTP step-up state
  const [stage, setStage] = useState("credentials");  // credentials | otp
  const [otpUserId, setOtpUserId] = useState("");
  const [mobileHint, setMobileHint] = useState("");
  const [otp, setOtp] = useState("");
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u?.otp_required) {
        setMobileHint(u.mobile_hint || "");
        setOtpUserId(u.user_id || "");
        setStage("otp");
        setResendIn(45);
        toast.info(`OTP sent to your registered mobile ending in ****${u.mobile_hint}`);
        return;
      }
      if (u.role === "admin") {
        await logout();
        toast.error("Administrators must use the admin portal URL.");
        return;
      }
      toast.success(`Welcome back, ${u.name}!`);
      const dest = u.role === "volunteer" ? "/volunteer"
        : u.role === "ambassador" ? "/ambassador"
        : "/student";
      navigate(location.state?.from?.pathname || dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? formatApiError(detail) : `Login failed`);
    } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (otp.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    if (!otpUserId) {
      toast.error("Session expired — please login again");
      setStage("credentials");
      return;
    }
    setLoading(true);
    try {
      const u = await verifyLoginOtp(otpUserId, otp);
      toast.success(`Welcome back, ${u.name}!`);
      const dest = u.role === "volunteer" ? "/volunteer"
        : u.role === "ambassador" ? "/ambassador"
        : "/student";
      navigate(location.state?.from?.pathname || dest, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? formatApiError(detail) : "Incorrect OTP — please try again");
    } finally { setLoading(false); }
  };

  const resendOtp = async () => {
    // Ask backend to re-issue the OTP for the same login session. We
    // trigger the send by re-invoking /auth/login (same email/password)
    // which will resend a fresh OTP to the registered mobile.
    if (!email || !password) {
      toast.error("Session expired — please login again");
      setStage("credentials");
      return;
    }
    try {
      const u = await login(email, password);
      if (u?.otp_required) {
        setOtpUserId(u.user_id || otpUserId);
        toast.success("OTP re-sent");
        setResendIn(45);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to resend");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-6">
          <Logo size={52} />
        </Link>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          {stage === "credentials" ? (
            <>
              <h1 className="heading text-2xl font-bold">Welcome back</h1>
              <p className="text-sm text-slate-500 mt-1">Login to access your PMR dashboard.</p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Email</label>
                  <div className="relative mt-1">
                    <Mail size={16} className="absolute left-3 top-3.5 text-slate-400" />
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-300 focus:border-brand outline-none"
                      data-testid="login-email-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Password</label>
                  <div className="relative mt-1">
                    <Lock size={16} className="absolute left-3 top-3.5 text-slate-400" />
                    <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-300 focus:border-brand outline-none"
                      data-testid="login-password-input" />
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-brand hover:bg-[#172b66] disabled:opacity-60 text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2"
                  data-testid="login-submit-button">
                  {loading && <Loader2 size={16} className="animate-spin" />} Login
                </button>
              </form>
              <div className="mt-3 text-right">
                <Link to="/forgot-password" className="text-xs text-brand font-semibold hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <div className="mt-6 text-center text-sm text-slate-600">
                New here?{" "}
                <Link to="/register" className="text-brand font-semibold" data-testid="login-to-register-link">Create an account</Link>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} className="text-amber-500" />
                <h1 className="heading text-2xl font-bold">Verify it&apos;s you</h1>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                For your security, we&apos;ve sent a 6-digit OTP to your registered mobile {mobileHint && <>ending in <b>****{mobileHint}</b></>}. Enter the OTP below to continue.
              </p>
              <form onSubmit={verifyOtp} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">OTP</label>
                  <input type="text" inputMode="numeric" maxLength={6} required value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder="6-digit code" autoFocus
                    className="mt-1 w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-amber-500 outline-none text-center text-lg font-mono tracking-[0.4em]"
                    data-testid="login-otp-input" />
                </div>
                <button type="submit" disabled={loading || otp.length !== 6}
                  className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-semibold rounded-lg py-3 flex items-center justify-center gap-2"
                  data-testid="login-otp-verify-btn">
                  {loading && <Loader2 size={16} className="animate-spin" />} Verify & Login
                </button>
              </form>
              <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                <button type="button" onClick={() => setStage("credentials")} className="hover:text-brand font-semibold">← Use different account</button>
                {resendIn > 0 ? (
                  <span>Resend in {resendIn}s</span>
                ) : (
                  <button type="button" onClick={resendOtp} className="text-brand font-semibold hover:underline" data-testid="login-otp-resend">Resend OTP</button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="mt-5 text-center text-xs text-slate-500">
          <Link to="/" className="hover:text-brand">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
